import {
  Action,
  AnyAction,
  combineReducers,
  configureStore,
  PreloadedState,
  Reducer,
  ThunkAction
} from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import academyOnboarding from 'redux/slices/academy-onboarding-slice'
import academy from 'redux/slices/academy-slice'
import academyTeaser from 'redux/slices/academy-teaser-slice'
import account from 'redux/slices/account-slice'
import type { AuthState } from 'redux/slices/auth-slice'
import auth from 'redux/slices/auth-slice'
import type { CartState } from 'redux/slices/cart-slice'
import cart from 'redux/slices/cart-slice'
import checkoutMenPower from 'redux/slices/checkout-men-power-slice'
import checkoutSexTools from 'redux/slices/checkout-sex-tools-slice'
import consultations from 'redux/slices/consultations-slice'
import creditCards from 'redux/slices/credit-cards-slice'
import cuidei from 'redux/slices/cuidei-slice'
import doctors from 'redux/slices/doctors-slice'
import type { HomeSliceState } from 'redux/slices/home-slice'
import home from 'redux/slices/home-slice'
import newConsultation from 'redux/slices/new-consultation-slice'
import onboarding from 'redux/slices/onboarding-slice'
import orders from 'redux/slices/orders-slice'
import packages from 'redux/slices/packages-slice'
import patientAreaUi from 'redux/slices/patient-area-ui-slice'
import phytotherapy from 'redux/slices/phytotherapy-slice'
import prepDocuments from 'redux/slices/prep-documents-slice'
import prescriptions from 'redux/slices/prescriptions-slice'
import singleItems from 'redux/slices/single-items-slice'
import subscriptions from 'redux/slices/subscriptions-slice'
import weightOnboarding from 'redux/slices/weight-onboarding-slice'
import storage from 'redux/store/persist-storage'
import { createTransform, FLUSH, PAUSE, PERSIST, persistReducer, PURGE, REGISTER, REHYDRATE } from 'redux-persist'

const sanitizeAuthState = (state?: Partial<AuthState>): AuthState => {
  const railsToken = state?.railsToken || null
  const nestToken = state?.nestToken || null

  return {
    isLoading: false,
    isLoggedIn: !!railsToken && !!nestToken,
    profile: null,
    railsToken,
    railsTokenType: state?.railsTokenType || null,
    railsRefreshToken: state?.railsRefreshToken || null,
    nestToken
  }
}

const sanitizeCartState = (state?: Partial<CartState>): CartState => ({
  packageCart: state?.packageCart || [],
  singleItemCart: state?.singleItemCart || [],
  cepData: null,
  deliveryType: null,
  deliveryPrice: 0,
  subscriptionPeriod: state?.subscriptionPeriod || null,
  shipmentData: [],
  packageOrderResponse: null,
  singleItemsCouponResponseData: null,
  singleItemsCouponCode: null,
  isCartDrawerOpen: false,
  isCheckoutDrawerOpen: false,
  isConfirmCancelOpen: false,
  isThankYouOpen: false,
  isAddressDrawerOpen: false
})

const sanitizeHomeState = (state?: Partial<HomeSliceState>): HomeSliceState => ({
  isLoading: false,
  activeStep: 1,
  packageData: {
    subscriptions: [],
    singles: []
  },
  selectedPackages: state?.selectedPackages || {},
  isShowingSingleDrugSelector: false,
  isShowingWeightHome: false,
  isAboutSubscriptionModalOpen: false,
  isGLP1UpsellModalOpen: false,
  isMounjaroLillyModalOpen: false,
  activePathologyTab: null,
  activeSpecialistTab: null,
  medleveConsultationType: undefined,
  medleveCommunicationMedium: state?.medleveCommunicationMedium
})

type PersistedState = Partial<{
  auth: Partial<AuthState>
  cart: Partial<CartState>
  home: Partial<HomeSliceState>
  cuidei: Record<string, unknown>
  _persist: {
    version: number
    rehydrated: boolean
  }
}>

const sanitizePersistedState = (state?: PersistedState): PersistedState | undefined => {
  if (!state) return state

  return {
    auth: sanitizeAuthState(state.auth),
    cart: sanitizeCartState(state.cart),
    home: sanitizeHomeState(state.home),
    // Persist the Cuidei flow (step + created consultation) so a reload mid-flow
    // resumes where the patient was. Only include it when present — emitting
    // `cuidei: undefined` would clobber the reducer's initial state on rehydrate.
    ...(state.cuidei ? { cuidei: state.cuidei } : {}),
    _persist: state._persist
  }
}

const authPersistTransform = createTransform(sanitizeAuthState, sanitizeAuthState, { whitelist: ['auth'] })
const cartPersistTransform = createTransform(sanitizeCartState, sanitizeCartState, { whitelist: ['cart'] })
const homePersistTransform = createTransform(sanitizeHomeState, sanitizeHomeState, { whitelist: ['home'] })

const persistConfig = {
  key: 'root',
  version: 2,
  storage,
  whitelist: ['auth', 'cart', 'home', 'cuidei'],
  transforms: [authPersistTransform, cartPersistTransform, homePersistTransform],
  migrate: (state: unknown) => Promise.resolve(sanitizePersistedState(state as PersistedState) as any)
}

const appReducer = combineReducers({
  academyOnboarding,
  academy,
  academyTeaser,
  account,
  auth,
  cart,
  checkoutMenPower,
  checkoutSexTools,
  consultations,
  creditCards,
  cuidei,
  doctors,
  home,
  newConsultation,
  onboarding,
  orders,
  packages,
  patientAreaUi,
  phytotherapy,
  prepDocuments,
  prescriptions,
  singleItems,
  subscriptions,
  weightOnboarding
})

// SECURITY: when the user signs out, reset EVERY slice — not just the auth
// slice. Previously the logout handler only dispatched signOut() (which
// reset the auth slice) plus resetPatientAreaUiState. The other ~22 slices
// kept their state in memory until the next page reload, so a fresh login
// on the same tab could see the previous user's data on first paint
// (consultations, prescriptions, prepDocuments, singleItems, etc.).
// LGPD incident — see security audit 2026-04-09.
const rootReducer: Reducer = (state, action: AnyAction) => {
  if (action.type === 'auth/signOut') {
    if (typeof window !== 'undefined') {
      try {
        storage.removeItem('persist:root')
        window.localStorage?.clear()
        window.sessionStorage?.clear()
      } catch (_) {
        // ignore — cleanup is best-effort, the in-memory reset below is the
        // important guarantee
      }
    }
    // Returning undefined makes combineReducers re-init every slice from
    // its `initialState`. Single source of truth for "wipe everything".
    return appReducer(undefined, action)
  }
  return appReducer(state, action)
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export function setupStore(preloadedState?: PreloadedState<RootState>) {
  return configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      })
  })
}

export type RootState = ReturnType<typeof appReducer>
export type AppStore = ReturnType<typeof setupStore>
export type AppDispatch = AppStore['dispatch']

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>

export const store = setupStore()
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
